import { createContext, type ReactNode, useContext, useState } from 'react';
import { useBangCanDoiPhatSinhCongNoExport } from './hooks/useBangCanDoiPhatSinhCongNoExport';
import { useDynamicSearch } from '@/hooks/use-dynamic-search';
import { getTableColumns } from './cols-definition';
import { useSearchFieldStates } from './hooks';
import { useReport, useRows } from '@/hooks';
import { QUERY_KEYS } from '@/constants';
import { initialValues } from './schema';

interface BangCanDoiPhatSinhCongNoContextValue {
  exportToExcel: () => Promise<void>;
  canExport: boolean;
  isExporting: boolean;
  queryParams: BanHangRecord | null;
  setQueryParams: (params: BanHangTableParams | null) => void;

  getReport: (params: BanHangTableParams) => Promise<void>;
  isLoading: boolean;
  data: BanHangRecord[];
  currentPage: number;
  handlePageChange: (page: number) => Promise<void>;
  totalItems: number;

  searchFieldStates: ReturnType<typeof useSearchFieldStates>;

  initialSearchDialogOpen: boolean;
  editPrintTemplateDialogOpen: boolean;
  showTable: boolean;
  searchParams: BanHangSearchParams;
  handleInitialSearch: (
    formData: BanHangPayload,
    getReport: (params: BanHangTableParams) => Promise<void>
  ) => Promise<void>;
  handleInitialSearchClose: () => void;
  handleSearchClick: () => void;
  handleEditPrintTemplateClick: () => void;
  handleClosePrintTemplateDialog: () => void;
  handleSavePrintTemplate: (template: BanHangPayload) => void;
  handleFixedColumnsClick: () => void;
  handleExportDataClick: () => void;

  selectedRowIndex: string | null;
  handleRowClick: (row: BanHangRecord) => void;

  searchState: {
    filterParams: BanHangSearchParams;
    searchResults: BanHangRecord[];
    totalCount: number;
    currentPage: number;
    isSearching: boolean;
  };
  handleSearchChange: (values: BanHangPayload) => void;
  handleSearchPageChange: (page: number) => void;
  hasFilterParams: boolean;
  handleInitialSearchLogic: (formData: BanHangPayload) => Promise<void>;
  handleRefreshClickLogic: () => Promise<void>;
  isSearching: boolean;
}

const BangCanDoiPhatSinhCongNoContext = createContext<BangCanDoiPhatSinhCongNoContextValue | null>(null);

export const useBangCanDoiPhatSinhCongNoContext = () => {
  const context = useContext(BangCanDoiPhatSinhCongNoContext);
  if (!context) {
    throw new Error('useBangCanDoiPhatSinhCongNoContext must be used within BangCanDoiPhatSinhCongNoProvider');
  }
  return context;
};

export const BangCanDoiPhatSinhCongNoProvider = ({ children }: { children: ReactNode }) => {
  const [queryParams, setQueryParams] = useState<BanHangRecord | null>(null);
  const { exportToExcel, canExport, isExporting } = useBangCanDoiPhatSinhCongNoExport(queryParams);
  const { getReport, isLoading, data, currentPage, handlePageChange, totalItems } = useReport<
    BanHangRecord,
    BanHangSearchParams
  >({
    endpoint: QUERY_KEYS.BANG_CAN_DOI_PHAT_SINH_CONG_NO,
    pageSize: 50
  });

  const searchFieldStates = useSearchFieldStates();
  const dialogState = useDialogState({ setQueryParams, exportToExcel });

  const { selectedRowIndex, handleRowClick } = useRows();
  const {
    searchState,
    handleSearchChange,
    handleSearchPageChange,
    hasFilterParams,
    handleInitialSearch: handleInitialSearchLogic,
    handleRefreshClick: handleRefreshClickLogic,
    isSearching
  } = useDynamicSearch({
    onSearch: getReport,
    params: dialogState.searchParams,
    onInitialSearch: dialogState.handleInitialSearch,
    getColumns: () => getTableColumns(() => {}),
    endpoint: QUERY_KEYS.BANG_CAN_DOI_PHAT_SINH_CONG_NO,
    pageSize: 50
  });

  return (
    <BangCanDoiPhatSinhCongNoContext.Provider
      value={{
        exportToExcel,
        canExport,
        isExporting,
        queryParams,
        setQueryParams,

        getReport,
        isLoading,
        data,
        currentPage,
        handlePageChange,
        totalItems,

        searchFieldStates,

        ...dialogState,

        selectedRowIndex,
        handleRowClick,

        searchState,
        handleSearchChange,
        handleSearchPageChange,
        hasFilterParams,
        handleInitialSearchLogic,
        handleRefreshClickLogic,
        isSearching
      }}
    >
      {children}
    </BangCanDoiPhatSinhCongNoContext.Provider>
  );
};

function useDialogState({
  setQueryParams,
  exportToExcel
}: {
  setQueryParams: (params: BanHangTableParams | null) => void;
  exportToExcel: () => Promise<void>;
}) {
  const [initialSearchDialogOpen, setInitialSearchDialogOpen] = useState(true);
  const [editPrintTemplateDialogOpen, setEditPrintTemplateDialogOpen] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [searchParams, setSearchParams] = useState<BanHangRecord>(initialValues);

  const handleInitialSearch = async (
    formData: BanHangPayload,
    getReport: (params: BanHangTableParams) => Promise<void>
  ) => {
    try {
      const searchState = {
        ...formData
      };

      const apiParams = {
        ...formData
      };

      setSearchParams(searchState);
      setShowTable(true);
      setQueryParams(apiParams);
      await getReport(apiParams);
      handleInitialSearchClose();
    } catch (error) {
      return;
    }
  };

  const handleInitialSearchClose = () => {
    setInitialSearchDialogOpen(false);
  };

  const handleSearchClick = () => {
    setInitialSearchDialogOpen(true);
  };

  const handleEditPrintTemplateClick = () => {
    setEditPrintTemplateDialogOpen(true);
  };

  const handleClosePrintTemplateDialog = () => {
    setEditPrintTemplateDialogOpen(false);
  };

  const handleSavePrintTemplate = (_template: BanHangPayload) => {
    setEditPrintTemplateDialogOpen(false);
  };

  const handleFixedColumnsClick = () => {};

  const handleExportDataClick = () => exportToExcel();

  return {
    initialSearchDialogOpen,
    editPrintTemplateDialogOpen,
    showTable,
    searchParams,
    handleInitialSearch,
    handleInitialSearchClose,
    handleSearchClick,
    handleEditPrintTemplateClick,
    handleClosePrintTemplateDialog,
    handleSavePrintTemplate,
    handleFixedColumnsClick,
    handleExportDataClick
  };
}
